import React, { useState } from 'react';
import { AppHeader } from '../components/AppHeader';
import type { AuthUser } from '../hooks/useAuth';

interface Props { user: AuthUser; }

interface QueueItem { id: string; type: string; label: string; icon: string; time: string; }

const QUEUE_KEY = 'catms_sms_queue';

const STAMP_TYPES = [
  { type: 'IN',         label: '出勤',     icon: '🟢', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  { type: 'OUT',        label: '退勤',     icon: '🔴', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  { type: 'REST_START', label: '休憩開始', icon: '☕', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  { type: 'REST_END',   label: '休憩終了', icon: '✅', color: '#0ea5e9', bg: '#eff6ff', border: '#bfdbfe' },
] as const;

export const SmsPage: React.FC<Props> = ({ user }) => {
  const smsNumber = '+81-XXX-XXXX-XXXX';
  const [queue, setQueue] = useState<QueueItem[]>(() => {
    try { return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]'); }
    catch { return []; }
  });
  const [sent, setSent] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const saveQueue = (q: QueueItem[]) => {
    setQueue(q);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  };

  const addToQueue = (type: string, label: string, icon: string) => {
    const now = new Date();
    const t = now.getFullYear() + '/' + String(now.getMonth()+1).padStart(2,'0') + '/' + String(now.getDate()).padStart(2,'0') + ' ' + String(now.getHours()).padStart(2,'0') + ':' + String(now.getMinutes()).padStart(2,'0');
    saveQueue([...queue, { id: Date.now().toString(), type, label, icon, time: t }]);
    setSent(null);
  };

  const removeFromQueue = (id: string) => { saveQueue(queue.filter(q => q.id !== id)); setDeleteId(null); };

  const sendSingle = (type: string, label: string) => {
    window.location.href = 'sms:' + smsNumber + '?body=' + encodeURIComponent(user.short_id + ' ' + type);
    setSent(label + ' を送信しました');
  };

  const sendBulk = () => {
    if (queue.length === 0) return;
    const body = queue.map(q => user.short_id + ' ' + q.type).join(';');
    window.location.href = 'sms:' + smsNumber + '?body=' + encodeURIComponent(body);
    setSent(queue.length + '件をまとめて送信しました');
    saveQueue([]);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
      <AppHeader
        title="SMS打刻"
        right={queue.length > 0 ? (
          <div style={{ flexShrink: 0, background: '#ef4444', color: 'white', borderRadius: 20, padding: '4px 11px', fontSize: 12, fontWeight: 800 }}>未送信 {queue.length}件</div>
        ) : undefined}
      />
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 14, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>📡</span>
          <div style={{ fontSize: 13, color: '#92400e', lineHeight: 1.6 }}>海上でインターネットが使えない場合、打刻をためておいて後でまとめてSMS送信できます。</div>
        </div>
        {sent && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 8 }}><span>✅</span><span>{sent}</span></div>}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', padding: '0 4px' }}>打刻をためる</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {STAMP_TYPES.map(b => (
            <button key={b.type} onClick={() => addToQueue(b.type, b.label, b.icon)} style={{ padding: '16px 12px', borderRadius: 14, border: '1px solid ' + b.border, background: b.bg, color: b.color, fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 26 }}>{b.icon}</span>{b.label}
              <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>キューに追加</span>
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', padding: '0 4px' }}>未送信の打刻 ({queue.length}件)</div>
        {queue.length === 0 ? (
          <div style={{ background: 'white', borderRadius: 14, padding: '24px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}><div style={{ fontSize: 32, marginBottom: 8 }}>📭</div><div style={{ fontSize: 13, color: '#94a3b8' }}>未送信の打刻はありません</div></div>
        ) : (
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            {queue.map((item, i) => (
              <React.Fragment key={item.id}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 12 }}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{item.icon}</span>
                  <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{item.label}</div><div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{item.time}</div></div>
                  <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#94a3b8', background: '#f8fafc', padding: '3px 8px', borderRadius: 6, border: '1px solid #e2e8f0' }}>{user.short_id} {item.type}</div>
                  <button onClick={() => setDeleteId(item.id)} style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: 18, cursor: 'pointer', padding: '0 2px' }}>✕</button>
                </div>
                {i < queue.length - 1 && <div style={{ height: 1, background: '#f1f5f9', marginLeft: 56 }} />}
              </React.Fragment>
            ))}
          </div>
        )}
        {queue.length > 0 && (
          <button onClick={sendBulk} style={{ width: '100%', padding: '16px', borderRadius: 14, border: 'none', background: '#0ea5e9', color: 'white', fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>📤</span>{queue.length}件をまとめてSMS送信
          </button>
        )}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', padding: '0 4px', marginTop: 4 }}>今すぐ1件送信</div>
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          {STAMP_TYPES.map((b, i) => (
            <React.Fragment key={b.type}>
              <div onClick={() => sendSingle(b.type, b.label)} style={{ display: 'flex', alignItems: 'center', padding: '13px 16px', gap: 14, cursor: 'pointer', background: 'white' }} onTouchStart={e => (e.currentTarget.style.background='#f8fafc')} onTouchEnd={e => (e.currentTarget.style.background='white')}>
                <span style={{ fontSize: 22 }}>{b.icon}</span>
                <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{b.label}</span>
                <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#94a3b8', background: '#f8fafc', padding: '3px 8px', borderRadius: 6, border: '1px solid #e2e8f0' }}>{user.short_id} {b.type}</span>
                <span style={{ color: '#0ea5e9', fontSize: 13, fontWeight: 700, marginLeft: 4 }}>送信</span>
              </div>
              {i < STAMP_TYPES.length - 1 && <div style={{ height: 1, background: '#f1f5f9', marginLeft: 54 }} />}
            </React.Fragment>
          ))}
        </div>
      </div>
      {deleteId && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-end', zIndex: 100 }}>
          <div style={{ background: 'white', width: '100%', borderRadius: '20px 20px 0 0', padding: '24px 20px 36px' }}>
            <div style={{ width: 40, height: 4, background: '#e2e8f0', borderRadius: 2, margin: '0 auto 20px' }} />
            <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', textAlign: 'center', marginBottom: 8 }}>この打刻を削除しますか？</div>
            <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', marginBottom: 24 }}>キューから削除されます。</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setDeleteId(null)} style={{ flex: 1, padding: '14px', borderRadius: 12, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>キャンセル</button>
              <button onClick={() => removeFromQueue(deleteId)} style={{ flex: 1, padding: '14px', borderRadius: 12, border: 'none', background: '#ef4444', color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>削除する</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
